import React, { useEffect, useRef, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { MentionsInput, Mention } from 'react-mentions';
import { compareStrings, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { UserName } from '../name_component/user/UserNameComponent';
import {
	AudioOutlined,
	CheckOutlined,
	DeleteOutlined,
	PauseOutlined,
	PlayCircleOutlined,
} from '@ant-design/icons';
import RecordingWaveform from './RecordingWaveform';

interface VoiceNoteRecorderProps {
	value: any;
	onChange: (value: any) => void;
	onTyping: () => void;
	handleSubmit: (e: any) => void;
	onSearch: (search: string, cb: (results: any[]) => void) => void;
	onSendAudio?: (blob: Blob, file: File) => void;
	onRecordingStateChange?: (isRecording: boolean, isPaused: boolean) => void;
}

const VoiceNoteRecorder = ({
	value,
	onChange,
	onTyping,
	handleSubmit,
	onSearch,
	onSendAudio,
	onRecordingStateChange,
}: VoiceNoteRecorderProps) => {
	const [isRecording, setIsRecording] = useState(false);
	const [isRecordingPaused, setIsRecordingPaused] = useState(false);
	const [hasRecording, setHasRecording] = useState(false);
	const [recordingDurationMs, setRecordingDurationMs] = useState(0);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const recordingChunksRef = useRef<Blob[]>([]);
	const recordingTimerRef = useRef<number | null>(null);
	const recordedBlobRef = useRef<Blob | null>(null);
	const isResettingRef = useRef<boolean>(false);
	const mediaStreamRef = useRef<MediaStream | null>(null);

	useEffect(() => {
		if (onRecordingStateChange) {
			onRecordingStateChange(isRecording, isRecordingPaused);
		}
	}, [isRecording, isRecordingPaused, onRecordingStateChange]);

	const clearRecordingTimer = () => {
		if (recordingTimerRef.current) {
			window.clearInterval(recordingTimerRef.current);
			recordingTimerRef.current = null;
		}
	};

	const stopMediaStream = () => {
		if (mediaStreamRef.current) {
			const tracks = mediaStreamRef.current.getTracks();
			tracks.forEach(function (track) {
				track.stop();
			});
			mediaStreamRef.current = null;
		}
	};

	const resetRecordingState = () => {
		isResettingRef.current = true;
		clearRecordingTimer();
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			try {
				mediaRecorderRef.current.stop();
			} catch (e) {
				// ignore
			}
		}
		stopMediaStream();
		mediaRecorderRef.current = null;
		recordingChunksRef.current = [];
		recordedBlobRef.current = null;
		setIsRecording(false);
		setIsRecordingPaused(false);
		setHasRecording(false);
		setRecordingDurationMs(0);
		// Reset the flag after state updates
		setTimeout(() => {
			isResettingRef.current = false;
		}, 0);
	};

	const startRecording = async () => {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			message.error('Audio recording is not supported in this browser.');
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaStreamRef.current = stream;
			const mediaRecorder = new MediaRecorder(stream);
			recordingChunksRef.current = [];

			mediaRecorder.ondataavailable = function (event) {
				if (event.data && event.data.size > 0) {
					recordingChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = function () {
				// Don't update state if we're in the middle of resetting
				if (isResettingRef.current) {
					return;
				}
				clearRecordingTimer();
				stopMediaStream();
				if (recordingChunksRef.current.length > 0) {
					const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
					recordedBlobRef.current = blob;
					setHasRecording(true);
				}
				setIsRecording(false);
				setIsRecordingPaused(false);
			};

			mediaRecorderRef.current = mediaRecorder;
			mediaRecorder.start();
			setIsRecording(true);
			setIsRecordingPaused(false);
			setHasRecording(false);
			setRecordingDurationMs(0);

			clearRecordingTimer();
			var startedAt = Date.now();
			recordingTimerRef.current = window.setInterval(function () {
				setRecordingDurationMs(Date.now() - startedAt);
			}, 250);
		} catch (err) {
			message.error('Unable to start audio recording. Please check microphone permissions.');
		}
	};

	const deleteRecording = () => {
		resetRecordingState();
	};

	const togglePauseRecording = () => {
		if (!mediaRecorderRef.current) {
			return;
		}
		if (mediaRecorderRef.current.state === 'recording') {
			try {
				mediaRecorderRef.current.pause();
			} catch (e) {
				return;
			}
			setIsRecordingPaused(true);
			clearRecordingTimer();
		} else if (mediaRecorderRef.current.state === 'paused') {
			try {
				mediaRecorderRef.current.resume();
			} catch (e) {
				return;
			}
			setIsRecordingPaused(false);
			var resumedAt = Date.now() - recordingDurationMs;
			clearRecordingTimer();
			recordingTimerRef.current = window.setInterval(function () {
				setRecordingDurationMs(Date.now() - resumedAt);
			}, 250);
		}
	};

	const stopAndFinalizeRecording = () => {
		if (!mediaRecorderRef.current) {
			return;
		}
		if (mediaRecorderRef.current.state !== 'inactive') {
			try {
				mediaRecorderRef.current.stop();
			} catch (e) {
				// ignore
			}
		}
	};

	const handleConfirmRecording = () => {
		// If still recording, stop it first
		if (isRecording && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			stopAndFinalizeRecording();
			// Wait for onstop to fire and create the blob
			// We'll use a small delay to ensure the blob is ready
			setTimeout(() => {
				if (recordedBlobRef.current && onSendAudio) {
					var blob: Blob = recordedBlobRef.current;
					var fileName = 'voice-note.webm';
					var file = new File([blob], fileName, { type: blob.type || 'audio/webm' });
					onSendAudio(blob, file);
				}
				resetRecordingState();
			}, 100);
			return;
		}

		if (!recordedBlobRef.current) {
			// if user clicks very quickly after stopping, blob may not be ready yet
			stopAndFinalizeRecording();
			setTimeout(() => {
				if (recordedBlobRef.current && onSendAudio) {
					var blob: Blob = recordedBlobRef.current;
					var fileName = 'voice-note.webm';
					var file = new File([blob], fileName, { type: blob.type || 'audio/webm' });
					onSendAudio(blob, file);
				}
				resetRecordingState();
			}, 100);
			return;
		}

		if (onSendAudio && recordedBlobRef.current) {
			var blob: Blob = recordedBlobRef.current;
			var fileName = 'voice-note.webm';
			var file = new File([blob], fileName, { type: blob.type || 'audio/webm' });
			onSendAudio(blob, file);
		}
		resetRecordingState();
	};

	const hasText = value && value.trim && value.trim().length > 0;

	const formatDuration = (ms: number) => {
		var totalSeconds = Math.floor(ms / 1000);
		var minutes = Math.floor(totalSeconds / 60);
		var seconds = totalSeconds % 60;
		var paddedSeconds = seconds < 10 ? '0' + seconds : seconds;
		return minutes + ':' + paddedSeconds;
	};

	return (
		<>
			<div className="w-full">
				{isRecording || hasRecording ? (
					<div className="flex items-center justify-between gap-4 border border-solid border-zinc-200 bg-white px-4 py-2">
						<div className="flex items-center gap-2">
							<Tooltip title="Cancel">
								<Button
									shape="circle"
									onClick={deleteRecording}
									icon={<DeleteOutlined style={{ fontSize: 20 }} />}
									className="bg-character-danger text-white"
								/>
							</Tooltip>
						</div>
						{isRecording && (
							<div className="flex items-center gap-2">
								<RecordingWaveform
									stream={mediaStreamRef.current}
									isPaused={isRecordingPaused}
									durationMs={recordingDurationMs}
									onPauseToggle={togglePauseRecording}
								/>
								<div className="flex items-center gap-1">
									<Tooltip title={isRecordingPaused ? 'Resume' : 'Pause'}>
										<Button
											shape="circle"
											onClick={togglePauseRecording}
											icon={
												isRecordingPaused ? (
													<AudioOutlined style={{ fontSize: 20 }} />
												) : (
													<PauseOutlined style={{ fontSize: 20 }} />
												)
											}
										/>
									</Tooltip>
								</div>
							</div>
						)}
					</div>
				) : (
					<MentionsInput
						placeholder="Add a note to this work order."
						forceSuggestionsAboveCursor
						value={value}
						onChange={function (e, newValue) {
							onChange(newValue);
						}}
						className="chat-input  ant-input"
						allowSpaceInQuery={true}
						onKeyDown={function (e) {
							onTyping();
							if (e.key === 'Enter' && !e.shiftKey) {
								stopDefaultEvents(e);
								handleSubmit(e);
							}
						}}
					>
						<Mention
							trigger="@"
							data={onSearch}
							className="bg-[#d2d9de]"
							renderSuggestion={function (user, search, highlightedDisplay, index, focused) {
								var isInactive = compareStrings(user.status, 'inactive');
								return (
									<div
										className={
											'flex flex-row items-center p-2 px-4 ' +
											(isInactive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')
										}
										onClick={function (e) {
											if (isInactive) {
												e.preventDefault();
												e.stopPropagation();
											}
										}}
									>
										<UserName
											contact={user}
											onClick={function () {}}
											status={user.status === 'not_available' ? undefined : user.status}
										/>
									</div>
								);
							}}
						/>
					</MentionsInput>
				)}
			</div>
			<div className="flex items-end bg-zinc-50 px-3 pb-2">
				{!hasText ? (
					isRecording || hasRecording ? (
						<Tooltip title="Done">
							<Button
								shape="circle"
								type="primary"
								onClick={handleConfirmRecording}
								icon={<CheckOutlined style={{ fontSize: 20 }} />}
							/>
						</Tooltip>
					) : (
						<Tooltip title="Record voice note">
							<Button
								shape="circle"
								style={{ border: 'none' }}
								onClick={function () {
									if (isRecording || hasRecording) {
										return;
									}
									startRecording();
								}}
								icon={
									<AudioOutlined
										className="stroke-character-secondary text-character-secondary"
										style={{ fontSize: 20 }}
									/>
								}
							/>
						</Tooltip>
					)
				) : (
					<Tooltip title="Send">
						<Button htmlType="submit" shape="circle" style={{ border: 'none' }}>
							<i style={{ fontSize: 20 }} className="icons8-font icons8-paper-airplane"></i>
						</Button>
					</Tooltip>
				)}
			</div>
		</>
	);
};

export default VoiceNoteRecorder;
